import { useDesignSystem } from 'design-system';
import Card, { CARD_VARIANTS } from 'components/atomic/atoms/card';
import FlexContainer from 'components/atomic/atoms/flex-container';
import Text from 'components/atomic/atoms/text';
import CheckmarkCircleIcon from 'components/icon/checkmark-circle-icon';
import { Box } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';

export interface ConnectionRequestSuccessCardStrings {
  thanksLabel: string;
  followUpLabel: string;
  callUsLabel?: string;
}

type ConnectionRequestSuccessCardProps = {
  strings: ConnectionRequestSuccessCardStrings;
  phoneNumber?: string;
};

/**
 * ConnectionRequestSuccessCard
 *
 * UI Layer: src/ui
 *
 * Renders the post-submission confirmation in the sidebar, replacing the CTA card.
 * Matches the legacy sidebar success state: checkmark, "Thanks!", follow-up copy, phone CTA.
 * Receives all copy as pre-formatted strings — no i18n, no domain logic.
 */
export function ConnectionRequestSuccessCard({
  strings,
  phoneNumber,
}: ConnectionRequestSuccessCardProps) {
  const { theme, tokens: { typography, effects } } = useDesignSystem();

  return (
    <Card
      variant={CARD_VARIANTS.SHADOW}
      testId="connection-request-success-card"
      sx={{
        px: 3,
        py: '29px',
        borderRadius: theme.buttons.borderRadiusModal,
        boxShadow: effects.elevation[200],
      }}
    >
      <FlexContainer direction="row" align="center" gap={1.5}>
        <Box sx={{ flexShrink: 0, color: theme.colors.primary, '& path': { fill: theme.colors.primary } }}>
          <CheckmarkCircleIcon />
        </Box>

        <FlexContainer direction="column" align="flex-start" gap={0.5}>
          <Text
            component="p"
            variant="body1"
            sx={{
              fontFamily: theme.fonts.heading,
              fontWeight: typography.fontWeight.bold,
              fontSize: typography.fontSize.lg,
              color: theme.colors.primaryDark,
              lineHeight: typography.lineHeight.tight,
            }}
          >
            {strings.thanksLabel}
          </Text>

          <Text
            component="p"
            variant="body2"
            sx={{
              fontFamily: theme.fonts.text,
              fontSize: typography.fontSize.md,
              color: theme.colors.primaryDark,
              lineHeight: typography.lineHeight.loose,
            }}
          >
            {strings.followUpLabel}
          </Text>

          {phoneNumber && strings.callUsLabel && (
            <Box
              component="a"
              href={`tel:${phoneNumber}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                mt: 0.5,
                color: theme.colors.primaryDark,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <PhoneIcon sx={{ fontSize: '11px' }} />
              <Text
                component="span"
                variant="body2"
                sx={{
                  fontFamily: theme.fonts.text,
                  fontSize: typography.fontSize.md,
                  color: theme.colors.primaryDark,
                }}
              >
                {strings.callUsLabel}
              </Text>
            </Box>
          )}
        </FlexContainer>
      </FlexContainer>
    </Card>
  );
}
